import { supabaseClient } from "@/lib/supabase";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/utils/auth";
import { filterStaffBySecretaryOwnership, selectLinkedSecretaryIds } from "./filterStaffBySecretaryOwnership";
import { selectProfilesByRoles } from "./selectProfilesByRoles";

/** Default staff picker roles (clinician workflows, favourites, etc.). */
export const CLINICAL_STAFF_ROLES = ["CLINICIAN", "SECRETARY"] as const;

/** Share Template may target managers so they can attach copies on studies. */
export const SHARE_TEMPLATE_STAFF_ROLES = ["CLINICIAN", "SECRETARY", "MANAGER"] as const;

/** Share Analytics View may also target admins; the edge function already accepts them. */
export const SHARE_ANALYTICS_STAFF_ROLES = ["CLINICIAN", "SECRETARY", "MANAGER", "ADMIN"] as const;

export interface SelectStaffOptions {
    /** Skip the "only my linked secretaries" filter, e.g. for admins who may share with any secretary. */
    includeAllSecretaries?: boolean;
}

const selectStaff = async (
    viewerId: string,
    roles: readonly string[] = CLINICAL_STAFF_ROLES,
    options: SelectStaffOptions = {}
) => {
    const [data, linkedSecretaryIds, profileResult] = await Promise.all([
        selectProfilesByRoles({ roles }),
        selectLinkedSecretaryIds(viewerId),
        supabaseClient.from("profiles").select("is_super_user, roles").eq("user_id", viewerId).single(),
    ]);
    if (profileResult.error) throw profileResult.error;

    const skipFilter = Boolean(profileResult.data?.is_super_user) || Boolean(options.includeAllSecretaries);
    return filterStaffBySecretaryOwnership(data, linkedSecretaryIds, { skipFilter });
};

export const selectStaffQueryKey = ["select-staff"] as const;

export const useSelectStaff = (roles: readonly string[] = CLINICAL_STAFF_ROLES, options: SelectStaffOptions = {}) => {
    const auth = useAuth();
    return useQuery({
        queryKey: [...selectStaffQueryKey, auth.id, ...roles, Boolean(options.includeAllSecretaries)],
        queryFn: () => selectStaff(auth.id, roles, options),
        enabled: Boolean(auth.id),
    });
};
